import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { History, RotateCcw, AlertCircle, CheckCircle, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

function VersionHistory() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [rollbackComment, setRollbackComment] = useState('');

  const storedConfig = JSON.parse(localStorage.getItem('qianshu_config') || '{}');

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getVersions(storedConfig.appId, 50, 0);
      setVersions(data.data.versions);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (version) => {
    if (!confirm(`确定要回滚到版本 ${version} 吗？`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await apiClient.rollback(storedConfig.appId, version, rollbackComment || `回滚到版本 ${version}`);
      setSuccess(`成功回滚到版本 ${version}`);
      setRollbackComment('');
      await loadVersions();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (version) => {
    if (!confirm(`确定要删除版本 ${version} 吗？此操作不可恢复！`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await apiClient.deleteVersion(storedConfig.appId, version);
      setSuccess(`已删除版本 ${version}`);
      await loadVersions();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const viewVersion = async (version) => {
    try {
      const data = await apiClient.getConfigVersion(storedConfig.appId, version);
      setSelectedVersion(data.data);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">版本历史</h1>
        <button
          onClick={loadVersions}
          className="btn-secondary"
        >
          刷新
        </button>
      </div>

      {error && (
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center text-red-800">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {/* 版本列表 */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">所有版本</h2>
        <div className="space-y-3">
          {versions.map((version) => (
            <div
              key={version.version}
              className={`border rounded-lg p-4 ${
                version.is_active === 1 ? 'border-green-500 bg-green-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <History className="w-5 h-5 mr-2 text-gray-500" />
                    <span className="text-lg font-semibold text-gray-900">
                      版本 {version.version}
                    </span>
                    {version.is_active === 1 && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-600 text-white rounded">
                        当前生效
                      </span>
                    )}
                  </div>
                  
                  {version.comment && (
                    <p className="text-sm text-gray-700 mb-2">
                      📝 {version.comment}
                    </p>
                  )}
                  
                  <p className="text-sm text-gray-500">
                    {format(new Date(version.created_at * 1000), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => viewVersion(version.version)}
                    className="btn-secondary text-sm py-1.5 px-3 flex items-center"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    查看
                  </button>
                  
                  {version.is_active !== 1 && (
                    <>
                      <button
                        onClick={() => handleRollback(version.version)}
                        className="btn-primary text-sm py-1.5 px-3 flex items-center"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        回滚
                      </button>
                      <button
                        onClick={() => handleDelete(version.version)}
                        className="text-red-600 hover:bg-red-50 border border-red-300 rounded-lg text-sm py-1.5 px-3 flex items-center transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        删除
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {versions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              暂无版本记录
            </div>
          )}
        </div>
      </div>

      {/* 版本详情模态框 */}
      {selectedVersion && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedVersion(null)}
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  版本 {selectedVersion.version} 详情
                </h3>
                <button
                  onClick={() => setSelectedVersion(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {selectedVersion.comment && (
                <p className="text-sm text-gray-700 mb-4">
                  📝 {selectedVersion.comment}
                </p>
              )}

              <p className="text-sm text-gray-500 mb-4">
                创建时间: {format(new Date(selectedVersion.created_at * 1000), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })}
              </p>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">配置内容</h4>
                <pre className="text-sm overflow-x-auto">
                  {JSON.stringify(selectedVersion.config, null, 2)}
                </pre>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setSelectedVersion(null)}
                  className="btn-secondary"
                >
                  关闭
                </button>
                {selectedVersion.version !== versions.find(v => v.is_active === 1)?.version && (
                  <button
                    onClick={() => {
                      setSelectedVersion(null);
                      handleRollback(selectedVersion.version);
                    }}
                    className="btn-primary flex items-center"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    回滚到此版本
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VersionHistory;
